import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import EmailDomainWarning from "../components/auth/EmailDomainWarning";
import { validateBusinessEmail } from "../utils/validators";
import { AuthContext } from "../context/AuthContext";
import { decidePostLoginPath } from "../utils/postLoginRedirect";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

/**
 * LoginPage — two-layer fullscreen layout.
 *
 *   Layer 1 (z-1): fullscreen MP4 video background, no overlay.
 *   Layer 2 (z-2): pointer-events:none container holding two floating
 *                  pointer-events:auto panels — the login form (upper)
 *                  and the "Take a quick tour" panel (lower).
 *
 * All login handlers, OTP flow, and the demo-code modal are unchanged
 * from the previous revision — only the surrounding chrome is new.
 */
export default function LoginPage() {
  const { login } = useContext(AuthContext);
  const navigate  = useNavigate();

  // ── Login + OTP state (unchanged) ───────────────────────────
  const [email, setEmail]               = useState("");
  const [password, setPassword]         = useState("");
  const [warning, setWarning]           = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState("");
  const [step, setStep]                 = useState("password");
  const [sessionToken, setSessionToken] = useState("");
  const [qrImage, setQrImage]           = useState("");
  const [otp, setOtp]                   = useState("");
  const [countdown, setCountdown]       = useState(60);
  const countdownRef                    = useRef(null);
  const otpInputRef                     = useRef(null);

  // ── "Enter demo code" modal (unchanged) ─────────────────────
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoCode,      setDemoCode]      = useState("");
  const [demoError,     setDemoError]     = useState("");
  const [demoLoading,   setDemoLoading]   = useState(false);
  const demoInputRef                      = useRef(null);

  const openDemoModal = () => {
    setDemoCode(""); setDemoError(""); setDemoLoading(false);
    setShowDemoModal(true);
    setTimeout(() => demoInputRef.current?.focus(), 80);
  };
  const closeDemoModal = () => setShowDemoModal(false);

  const submitDemoCode = async (e) => {
    e?.preventDefault?.();
    const code = (demoCode || "").trim().toUpperCase();
    if (!code) { setDemoError("Please enter your demo code."); return; }
    setDemoLoading(true); setDemoError("");
    try {
      const res = await fetch(`${API_BASE}/api/demo/access/${encodeURIComponent(code)}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = body?.detail || `Code rejected (HTTP ${res.status})`;
        setDemoError(typeof msg === "string" ? msg : "Code not valid");
        setDemoLoading(false);
        return;
      }
      setDemoLoading(false);
      navigate(`/demo/${code}`);
    } catch {
      setDemoError("Network error — couldn't reach the demo service.");
      setDemoLoading(false);
    }
  };

  useEffect(() => () => clearInterval(countdownRef.current), []);

  const checkEmail = (v) => { setEmail(v); setWarning(!validateBusinessEmail(v)); };

  const startCountdown = () => {
    setCountdown(60);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setError("Code expired. Please log in again.");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const finishLogin = async (data) => {
    login(data.token, data.user, data.refresh_token);
    try { sessionStorage.removeItem("kira_welcomed"); } catch {}
    // Decide between /onboarding (new starter) and /welcome (existing user).
    // Superadmin goes to /superadmin regardless. See utils/postLoginRedirect.js.
    const dest = await decidePostLoginPath({
      user: data.user,
      token: data.token,
      is_first_login: data.is_first_login,
    });
    // eslint-disable-next-line no-console
    console.log("[login] navigating to:", dest);
    if (dest !== "/superadmin") {
      sessionStorage.setItem("kira_from_login", "true");
      // The `diary_morning_pending` flag is gone: the scripted login
      // check-in it triggered has been retired. The welcome page's own
      // conversation is the morning diary entry now, saved on dismiss, so
      // there is exactly one morning write per day. Both post-login flows
      // converge on /welcome (see utils/postLoginRedirect.js), and
      // superadmin is excluded here as it always was.
    }
    window.location.href = dest;
  };

  const handleLogin = async () => {
    if (!email || !password) { setError("Email & password are required."); return; }
    setLoading(true); setError("");
    try {
      const res  = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Invalid email or password"); setLoading(false); return; }
      if (data.token && data.user) { finishLogin(data); return; }
      if (data.status === "otp_required") {
        setSessionToken(data.session_token);
        setQrImage(data.qr_image || "");
        setStep("otp"); startCountdown();
        setTimeout(() => otpInputRef.current?.focus(), 150);
      }
    } catch { setError("Network error — backend unreachable"); }
    setLoading(false);
  };

  const handleVerify = async () => {
    if (otp.length !== 6) { setError("Please enter the full 6-digit code."); return; }
    setLoading(true); setError("");
    try {
      const res  = await fetch(
        `${API_BASE}/api/auth/otp-verify?session_token=${sessionToken}&otp=${otp}`,
        { method: "POST", credentials: "include" },
      );
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Invalid code."); setLoading(false); return; }
      if (data.token && data.user) {
        clearInterval(countdownRef.current);
        finishLogin(data);
      }
    } catch { setError("Network error."); }
    setLoading(false);
  };

  const handleRetry = () => {
    clearInterval(countdownRef.current);
    setStep("password"); setSessionToken(""); setQrImage(""); setOtp(""); setError("");
  };

  return (
    <div style={OUTER_STYLE}>

      {/* ─────────────── LAYER 1 — split background ─────────────── */}
      {/* Left half: brand MP4. Right half: solid dark. The two panels in
          Layer 2 are centred horizontally so they straddle this seam. */}
      <div style={{
        position:   "absolute",
        top:        0,
        left:       0,
        width:      "50%",
        height:     "100%",
        overflow:   "visible",
        background: "#000000",
        zIndex:     1,
      }}>
        <video
          autoPlay muted playsInline loop
          src="/media/kira-intro.mp4"
          style={{
            width:          "100%",
            height:         "100%",
            objectFit:      "visible",
            objectPosition: "left center",
          }}
        />
      </div>
      <div style={{
        position:   "absolute",
        top:        0,
        right:      0,
        width:      "40%",
        height:     "100%",
        background: "#000000",
        zIndex:     1,
      }} />

      {/* ─────────────── LAYER 2 — centred floating panels ─────────────── */}
      <div style={{
        position:       "absolute",
        inset:          0,
        zIndex:         2,
        display:        "flex",
        flexDirection:  "column",
        alignItems:     "flex-end",
        justifyContent: "flex-start",
        gap:            "clamp(8px, 1.5vh, 16px)",
        paddingRight:   "clamp(16px, 3vw, 48px)",
        paddingTop:     "clamp(40px, 8vh, 80px)",
        pointerEvents:  "none",
      }}>

        {/* LOGIN PANEL */}
        <div
          className="kwery-panel-login"
          style={{
            pointerEvents:  "auto",
            flexShrink:     0,
            width:          "clamp(480px, 58%, 860px)",
            maxWidth:       900,
            background:     "rgba(8,8,8,0.82)",
            border:         "1px solid rgba(0,255,153,0.15)",
            borderRadius:   24,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            padding:        "clamp(24px, 3.5vh, 40px) clamp(32px, 4vw, 60px)",
            display:        "flex",
            flexDirection:  "column",
            gap:            0,
          }}
        >
          {step === "password" && (
            <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }} style={{ width: "100%" }}>
              {/* Row 1 — brand on the left, "Sign In" on the right */}
              <div style={{
                display:        "flex",
                justifyContent: "space-between",
                alignItems:     "flex-start",
                marginBottom:   32,
                gap:            24,
                flexWrap:       "wrap",
              }}>
                <div>
                  <h1 style={{
                    fontFamily: "'Syne', sans-serif",
                    fontWeight: 700,
                    fontSize:   36,
                    color:      "#00FF99",
                    margin:     0,
                    letterSpacing: 0.4,
                    lineHeight: 1,
                  }}>
                    KweryAI
                  </h1>
                  <p style={{
                    fontSize:  14,
                    color:     "rgba(255,255,255,0.4)",
                    marginTop: 4,
                    marginBottom: 0,
                  }}>
                    Workforce Intelligence Platform
                  </p>
                </div>
                <h2 style={{
                  fontSize:   28,
                  fontWeight: 600,
                  color:      "#fff",
                  margin:     0,
                  lineHeight: 1.1,
                  alignSelf:  "flex-end",
                }}>
                  Sign In
                </h2>
              </div>

              {/* Row 2 — Email + Password side by side */}
              <div style={{
                display:             "grid",
                gridTemplateColumns: "1fr 1fr",
                gap:                 16,
                marginBottom:        20,
              }}>
                <div>
                  <input
                    type="email"
                    placeholder="Work Email"
                    value={email}
                    onChange={(e) => checkEmail(e.target.value)}
                    className="kwery-input"
                    style={{ ...INPUT_STYLE, marginBottom: 0, pointerEvents: "auto" }}
                  />
                  {warning && <div style={{ marginTop: 8 }}><EmailDomainWarning /></div>}
                </div>
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  className="kwery-input"
                  style={{ ...INPUT_STYLE, marginBottom: 0, pointerEvents: "auto" }}
                />
              </div>

              {error && (
                <p style={{
                  color:    "#ff8a8a",
                  fontSize: 13,
                  margin:   "0 0 14px 0",
                }}>
                  {error}
                </p>
              )}

              {/* Row 3 — full-width Continue button */}
              <button
                type="submit"
                disabled={loading}
                className="kwery-btn"
                style={{
                  ...BUTTON_STYLE,
                  width:         "100%",
                  height:        52,
                  padding:       0,
                  fontSize:      16,
                  fontWeight:    600,
                  borderRadius:  10,
                  marginTop:     0,
                  marginBottom:  0,
                  opacity:       loading ? 0.5 : 1,
                  pointerEvents: "auto",
                }}
              >
                {loading ? "Verifying…" : "Continue →"}
              </button>

              {/* Row 4 — Forgot password, right-aligned */}
              <div style={{
                textAlign: "right",
                marginTop: 10,
              }}>
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  style={{
                    background:    "transparent",
                    border:        "none",
                    padding:       0,
                    fontSize:      13,
                    color:         "rgba(255,255,255,0.3)",
                    cursor:        "pointer",
                    pointerEvents: "auto",
                  }}
                >
                  Forgot Password?
                </button>
              </div>
            </form>
          )}

          {step === "otp" && (
            <div style={{
              width:     "100%",
              maxWidth:  480,
              margin:    "0 auto",
              textAlign: "center",
            }}>
              <h2 className="text-xl font-semibold text-white mb-1">Verify Your Identity</h2>
              <p className="text-gray-400 text-sm mb-5">
                Scan the QR code with your registered device.
              </p>
              {qrImage && (
                <div className="flex justify-center mb-5">
                  <div className="p-3 bg-white rounded-2xl inline-block shadow-xl">
                    <img src={`data:image/png;base64,${qrImage}`}
                      alt="Login QR Code" className="w-44 h-44" />
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-xs text-white/40 uppercase tracking-wider mb-2">
                  Enter 6-digit code
                </label>
                <input
                  ref={otpInputRef}
                  type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  onKeyDown={(e) => e.key === "Enter" && otp.length === 6 && handleVerify()}
                  className="w-full text-center text-3xl font-mono tracking-[0.5em] p-4 rounded-xl bg-black/40 border border-white/20 text-white outline-none focus:border-[#00FF99]/60 placeholder-white/20"
                  style={{ pointerEvents: "auto" }}
                  placeholder="——————" />
              </div>
              <p className={`text-xs mb-4 ${countdown < 60 ? "text-red-400" : "text-white/30"}`}>
                Expires in {formatTime(countdown)}
              </p>
              {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
              <button
                className="w-full bg-[#00FF99] text-black font-semibold p-3 rounded-xl hover:bg-[#00cc7a] transition disabled:opacity-50 mb-3"
                onClick={handleVerify} disabled={loading || otp.length !== 6}
                style={{ pointerEvents: "auto" }}>
                {loading ? "Verifying…" : "Verify & Sign In"}
              </button>
              <button onClick={handleRetry}
                className="text-sm text-white/30 hover:text-white transition underline"
                style={{ pointerEvents: "auto" }}>
                ← Start over
              </button>
            </div>
          )}
        </div>

        {/* TOUR PANEL */}
        <div
          className="kwery-panel-tour"
          style={{
            pointerEvents:  "auto",
            flexShrink:     0,
            width:          "clamp(480px, 58%, 860px)",
            maxWidth:       900,
            background:     "rgba(8,8,8,0.88)",
            border:         "1px solid rgba(0,255,153,0.15)",
            borderRadius:   24,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            padding:        "clamp(16px, 2.5vh, 28px) clamp(32px, 4vw, 64px)",
            display:        "flex",
            alignItems:     "center",
            justifyContent: "space-between",
            gap:            24,
          }}
        >
          <div>
            <div style={{
              fontSize:   22,
              fontWeight: 600,
              color:      "#00FF99",
            }}>
              Take a quick tour
            </div>
            <div style={{
              fontSize:  14,
              color:     "rgba(255,255,255,0.4)",
              marginTop: 6,
            }}>
              Experience KweryAI from every role — no sign up needed
            </div>
          </div>
          <button
            type="button"
            onClick={openDemoModal}
            className="kwery-btn"
            style={{
              background:    "#00FF99",
              color:         "#030303",
              border:        "none",
              borderRadius:  10,
              padding:       "14px 32px",
              fontSize:      14,
              fontWeight:    600,
              cursor:        "pointer",
              whiteSpace:    "nowrap",
              pointerEvents: "auto",
            }}
          >
            Enter demo code →
          </button>
        </div>

      </div>
      {/* ── /LAYER 2 */}

      {/* ─────────────── Demo-code modal (unchanged) ─────────────── */}
      {showDemoModal && (
        <div
          onClick={closeDemoModal}
          style={{
            position:       "fixed",
            inset:          0,
            zIndex:         100,
            background:     "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            padding:        16,
            pointerEvents:  "auto",
          }}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitDemoCode}
            style={{
              position:      "relative",
              width:         400,
              maxWidth:      "100%",
              background:    "#111",
              border:        "1px solid rgba(0,255,153,0.2)",
              borderRadius:  16,
              padding:       32,
              color:         "#fff",
              pointerEvents: "auto",
            }}
          >
            <button
              type="button"
              onClick={closeDemoModal}
              aria-label="Close"
              style={{
                position:      "absolute",
                top:           14,
                right:         14,
                width:         28,
                height:        28,
                borderRadius:  8,
                background:    "transparent",
                border:        "1px solid rgba(255,255,255,0.1)",
                color:         "rgba(255,255,255,0.5)",
                cursor:        "pointer",
                fontSize:      14,
                lineHeight:    1,
                pointerEvents: "auto",
              }}
            >
              ×
            </button>

            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#fff" }}>
              Enter your demo code
            </h3>
            <p style={{
              marginTop:    8,
              marginBottom: 20,
              fontSize:     13,
              color:        "rgba(255,255,255,0.5)",
              lineHeight:   1.5,
            }}>
              Your access code was sent by the KweryAI team.
            </p>

            <input
              ref={demoInputRef}
              type="text"
              placeholder="e.g. K7QM-P2RN"
              value={demoCode}
              onChange={(e) => setDemoCode(e.target.value.toUpperCase())}
              maxLength={20}
              className="kwery-input"
              style={{
                ...INPUT_STYLE,
                fontFamily:    "ui-monospace, SFMono-Regular, Menlo, monospace",
                letterSpacing: 2,
                textTransform: "uppercase",
                marginBottom:  0,
                pointerEvents: "auto",
              }}
            />

            {demoError && (
              <p style={{
                color:        "#ff8a8a",
                fontSize:     12,
                marginTop:    10,
                marginBottom: 0,
              }}>
                {demoError}
              </p>
            )}

            <button
              type="submit"
              disabled={demoLoading}
              className="kwery-btn"
              style={{
                ...BUTTON_STYLE,
                marginTop:     20,
                marginBottom:  0,
                opacity:       demoLoading ? 0.5 : 1,
                pointerEvents: "auto",
              }}
            >
              {demoLoading ? "Checking…" : "Access Demo →"}
            </button>
          </form>
        </div>
      )}

      {/* ─────────────── CSS ─────────────── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&display=swap');

        .kwery-input:focus {
          border-color: rgba(0,255,153,0.45);
          background:   rgba(255,255,255,0.09);
        }

        .kwery-btn {
          transition: opacity .15s ease, transform .15s ease;
        }
        .kwery-btn:hover:not(:disabled) {
          opacity:   0.92;
          transform: translateY(-1px);
        }

        @keyframes kfadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .kwery-panel-login {
          opacity: 0;
          animation: kfadeIn 0.5s ease forwards;
        }
        .kwery-panel-tour {
          opacity: 0;
          animation: kfadeIn 0.5s ease 0.15s forwards;
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Style constants
// ─────────────────────────────────────────────────────────────
const OUTER_STYLE = {
  position:   "fixed",
  inset:      0,
  overflow:   "hidden",
  background: "#000000",
};

const INPUT_STYLE = {
  width:        "100%",
  background:   "rgba(255,255,255,0.07)",
  border:       "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8,
  padding:      "13px 16px",
  color:        "#fff",
  fontSize:     14,
  outline:      "none",
  marginBottom: 12,
  fontFamily:   "inherit",
};

const BUTTON_STYLE = {
  width:        "100%",
  padding:      "13px",
  background:   "#00FF99",
  border:       "none",
  borderRadius: 8,
  fontSize:     15,
  fontWeight:   600,
  color:        "#030303",
  cursor:       "pointer",
  marginTop:    4,
  marginBottom: 8,
};
